import { useRef, useCallback, useState, useEffect } from 'react'
import Vapi from '@vapi-ai/web'
import { RetellWebClient } from 'retell-client-js-sdk'
import { apiFetch } from '../../api/client'

export type VapiCallStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnecting'
  | 'error'

export type AgentType = 'outbound' | 'inbound'

export type VoiceProvider = 'vapi' | 'retell'

export interface VapiMessage {
  id: number
  role: 'user' | 'assistant' | 'function_call'
  text: string
  final: boolean
  timestamp: string
  functionName?: string
}

export interface VapiEvent {
  id: number
  event: string
  timestamp: string
  payload?: unknown
}

let msgId = 0
let evtId = 0

/**
 * Discriminated config returned by ``POST /vapi/calls/web``. Phase 2
 * stamps a top-level ``provider`` field so the UI can branch SDK choice
 * without sniffing other keys.
 */
type WebCallConfig =
  | {
      provider: 'vapi'
      public_key: string
      assistant_id: string
      agent_type?: string
      // Legacy camelCase keys still present for backward compat. We don't
      // read them — snake_case is the canonical wire shape.
      publicKey?: string
      assistantId?: string
    }
  | {
      provider: 'retell'
      call_id: string
      access_token: string
      agent_id: string
      agent_type?: string
    }

/**
 * Discriminated active-client ref. Cleanup paths dispatch by the
 * ``provider`` tag because the two SDKs use different teardown method
 * names — VAPI exposes ``.stop()``, Retell exposes ``.stopCall()``.
 * Never call ``.stop()`` on a Retell client.
 */
type ActiveClient =
  | { provider: 'vapi'; instance: Vapi }
  | { provider: 'retell'; instance: RetellWebClient }

/**
 * Retell ``Utterance`` shape from the LLM WebSocket update protocol
 * (see https://docs.retellai.com/api-references/llm-websocket — the
 * ``update_only`` event). The client SDK's ``update`` event forwards
 * the same array as ``update.transcript``. We keep our own copy of the
 * type here so the hook compiles even when the SDK exposes the event
 * via bare ``EventEmitter`` (which it currently does).
 */
interface RetellUtterance {
  role: 'agent' | 'user' | 'transfer_target'
  content: string
  // ``words`` and other fields exist but we don't need them for the
  // dashboard transcript UI.
}

interface RetellUpdateEvent {
  event_type?: string
  transcript?: RetellUtterance[] | string
  turntaking?: 'agent_turn' | 'user_turn'
}

export function useVoiceClient() {
  const [agentType, setAgentType] = useState<AgentType>('outbound')
  const [status, setStatus] = useState<VapiCallStatus>('idle')
  const [provider, setProvider] = useState<VoiceProvider | null>(null)
  const [messages, setMessages] = useState<VapiMessage[]>([])
  const [events, setEvents] = useState<VapiEvent[]>([])
  const [isSpeaking, setIsSpeaking] = useState(false)
  const clientRef = useRef<ActiveClient | null>(null)

  // ── Retell transcript diffing state ───────────────────────────────
  // Retell's ``update`` event delivers a *rolling window* of the last 5
  // utterances — not turn-by-turn deltas like VAPI's ``transcript``
  // messages. To map onto the UI's interim-then-finalized rendering
  // (which the existing addMessage logic depends on), we synthesize one
  // finalized message per genuinely new utterance.
  //
  // The window rolls (after turn 6, the oldest item leaves the array),
  // and the trailing item is the in-progress utterance whose ``content``
  // grows across updates. So plain index- or content-equality diffing
  // misidentifies turns. Instead we track signatures (``role|content``)
  // of the most recently emitted finalized utterances in a ref Set —
  // sized large enough to span any plausible rolling window — and emit
  // an utterance only when its signature hasn't been seen.
  //
  // Refs (not state) because diffing must not trigger re-renders.
  const emittedSignaturesRef = useRef<Set<string>>(new Set())
  const emittedOrderRef = useRef<string[]>([])

  const addMessage = useCallback(
    (
      role: VapiMessage['role'],
      text: string,
      final = true,
      functionName?: string,
    ) => {
      const entry: VapiMessage = {
        id: msgId++,
        role,
        text,
        final,
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour12: false,
          fractionalSecondDigits: 3,
        }),
        functionName,
      }
      setMessages((prev) => {
        // Replace the last entry when it's a non-final from the same
        // role — whether the new entry is another partial (Retell's
        // rolling window grows the in-progress utterance in place across
        // many updates per turn) or the eventual final (VAPI's
        // promote-to-final). The previous ``if (final && ...)`` gate
        // let successive Retell partials accumulate as dozens of
        // orphan bubbles per utterance. This is a deliberate behaviour
        // change on the VAPI path too — if VAPI ever emits >1 partial
        // per turn the same replacement applies, which is the visually
        // correct outcome.
        if (prev.length > 0) {
          const last = prev[prev.length - 1]
          if (last.role === role && !last.final) {
            return [...prev.slice(0, -1), entry]
          }
        }
        const updated = [...prev, entry]
        return updated.length > 200 ? updated.slice(-200) : updated
      })
    },
    [],
  )

  const addEvent = useCallback((event: string, payload?: unknown) => {
    const entry: VapiEvent = {
      id: evtId++,
      event,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour12: false,
        fractionalSecondDigits: 3,
      }),
      payload,
    }
    setEvents((prev) => {
      const updated = [...prev, entry]
      return updated.length > 300 ? updated.slice(-300) : updated
    })
  }, [])

  /** Map Retell's role names onto the UI's role union. */
  const mapRetellRole = (role: RetellUtterance['role']): 'user' | 'assistant' =>
    role === 'user' ? 'user' : 'assistant'

  /** Stable signature for a (role, content) tuple — drives dedupe.
   *
   * Edge case: if the same content recurs (e.g. user says "yes" at
   * turn 3 and again at turn 27), the second occurrence will be
   * suppressed when both signatures live in the 32-entry dedupe ring.
   * In practice the ring evicts old entries fast enough that this
   * matters only for duplicates within ~32 turns, which is acceptable
   * for a transcript preview pane. */
  const utteranceSignature = (role: string, content: string): string =>
    `${role}|${content}`

  /**
   * Process a Retell ``update`` event payload. Splits the window into
   * finalized prefix + in-progress trailing item, emits each
   * never-before-seen finalized utterance as ``final=true``, and emits
   * the trailing partial as ``final=false`` so the UI shows a live
   * preview that gets replaced when the next update finalizes it.
   */
  const handleRetellUpdate = useCallback(
    (update: RetellUpdateEvent) => {
      const raw = update.transcript
      if (!raw) return

      // Defensive: docs guarantee Utterance[] for the LLM WebSocket
      // protocol, but the public SDK docs sometimes describe
      // ``update.transcript`` as a string. If we ever receive a string
      // (legacy format), log it once and bail rather than guessing how
      // to split it — better to drop transcript display than render
      // garbled merged turns.
      if (typeof raw === 'string') {
        addEvent('retell.update.unhandled_string_transcript', { sample: raw.slice(0, 200) })
        return
      }
      if (!Array.isArray(raw) || raw.length === 0) return

      // Trailing item is in-progress; everything before is finalized.
      // (When the call ends Retell may include the last utterance as
      // finalized — but in that case ``call_ended`` already fires and
      // the partial-→-final replacement just no-ops cleanly.)
      const finalizedItems = raw.slice(0, -1)
      const trailing = raw[raw.length - 1]

      const seen = emittedSignaturesRef.current
      const order = emittedOrderRef.current

      for (const utt of finalizedItems) {
        if (!utt || typeof utt.content !== 'string') continue
        if (utt.role === 'transfer_target') continue // not user-visible
        const sig = utteranceSignature(utt.role, utt.content)
        if (seen.has(sig)) continue
        seen.add(sig)
        order.push(sig)
        // Bound the dedupe ref so a long call doesn't accumulate. The
        // rolling window is 5 — keeping 32 is more than enough headroom
        // to span any reordering while preventing unbounded growth.
        while (order.length > 32) {
          const evicted = order.shift()
          if (evicted) seen.delete(evicted)
        }
        addMessage(mapRetellRole(utt.role), utt.content, true)
      }

      // Trailing in-progress utterance — render as a partial. The
      // existing addMessage logic replaces a same-role non-final with
      // each successive partial, then with the eventual final.
      if (trailing && typeof trailing.content === 'string' && trailing.content.length > 0) {
        if (trailing.role === 'transfer_target') return
        const sig = utteranceSignature(trailing.role, trailing.content)
        // Don't re-emit a partial whose content has *already* been
        // promoted to finalized in a prior update (rare but possible
        // when Retell promotes mid-update).
        if (seen.has(sig)) return
        addMessage(mapRetellRole(trailing.role), trailing.content, false)
      }
    },
    [addEvent, addMessage],
  )

  /** Reset diffing state at the start of every call. */
  const resetTranscriptDiff = useCallback(() => {
    emittedSignaturesRef.current = new Set()
    emittedOrderRef.current = []
  }, [])

  const connect = useCallback(async () => {
    setStatus('connecting')
    setMessages([])
    setEvents([])
    resetTranscriptDiff()
    addEvent('connecting')

    try {
      // Fetch config from our backend. Provider discriminator on the
      // response decides which SDK to load.
      const config = await apiFetch<WebCallConfig>('/vapi/calls/web', {
        method: 'POST',
        body: JSON.stringify({ agent_type: agentType }),
      })

      if (config.provider === 'retell') {
        setProvider('retell')

        const retell = new RetellWebClient()
        clientRef.current = { provider: 'retell', instance: retell }

        retell.on('call_started', () => {
          setStatus('connected')
          addEvent('call_started')
        })

        retell.on('call_ready', () => {
          addEvent('call_ready')
        })

        retell.on('call_ended', () => {
          setStatus('idle')
          addEvent('call_ended')
          setIsSpeaking(false)
          if (clientRef.current?.provider === 'retell') {
            clientRef.current = null
          }
        })

        // NOTE: VAPI's ``speech-start`` fires when the USER starts
        // speaking; Retell's ``agent_start_talking`` fires when the
        // AGENT starts speaking. We map both onto a single
        // ``isSpeaking`` boolean — the UI indicator becomes "voice
        // activity is happening" rather than strictly "you are
        // speaking". The existing copy in
        // VapiVoiceTestConsole.tsx ("Speaking" / "Listening") still
        // reads reasonably either way and the migration brief
        // (Phase 4) explicitly accepts this semantic divergence.
        retell.on('agent_start_talking', () => {
          setIsSpeaking(true)
          addEvent('agent_start_talking')
        })

        retell.on('agent_stop_talking', () => {
          setIsSpeaking(false)
          addEvent('agent_stop_talking')
        })

        retell.on('update', (update: RetellUpdateEvent) => {
          addEvent('update', update)
          handleRetellUpdate(update)
        })

        retell.on('metadata', (metadata: unknown) => {
          addEvent('metadata', metadata)
        })

        retell.on('node_transition', (transition: unknown) => {
          addEvent('node_transition', transition)
        })

        retell.on('error', (err: unknown) => {
          addEvent('error', { message: typeof err === 'string' ? err : String(err) })
          setStatus('error')
          if (clientRef.current?.provider === 'retell') {
            clientRef.current = null
          }
        })

        await retell.startCall({ accessToken: config.access_token })
        return
      }

      // VAPI branch (default). Unchanged from the pre-Phase-4 hook
      // beyond the provider tagging and discriminated ref — VAPI orgs
      // see exactly the same UX.
      setProvider('vapi')

      const vapi = new Vapi(config.public_key)
      clientRef.current = { provider: 'vapi', instance: vapi }

      vapi.on('call-start', () => {
        setStatus('connected')
        addEvent('call-start')
      })

      vapi.on('call-end', () => {
        setStatus('idle')
        addEvent('call-end')
        if (clientRef.current?.provider === 'vapi') {
          clientRef.current = null
        }
      })

      vapi.on('speech-start', () => {
        setIsSpeaking(true)
        addEvent('user.startedSpeaking')
      })

      vapi.on('speech-end', () => {
        setIsSpeaking(false)
        addEvent('user.stoppedSpeaking')
      })

      vapi.on('message', (msg: Record<string, unknown>) => {
        const type = msg.type as string
        addEvent(`message.${type}`, msg)

        if (type === 'transcript') {
          const role = (msg.role as string) === 'user' ? ('user' as const) : ('assistant' as const)
          const text = msg.transcript as string
          const isFinal = msg.transcriptType === 'final'
          addMessage(role, text, isFinal)
        } else if (type === 'function-call') {
          const fnName = ((msg.functionCall as Record<string, unknown>)?.name as string) ?? 'tool'
          addMessage('function_call', '', true, fnName)
        } else if (type === 'conversation-update') {
          // Full conversation state from VAPI — we rely on transcript events instead
        }
      })

      vapi.on('error', (err: Record<string, unknown>) => {
        addEvent('error', err)
        setStatus('error')
      })

      // (We skip subscribing to ``volume-level``: it fires continuously
      // and would only drive a visualizer, which we don't render today.)

      await vapi.start(config.assistant_id)
    } catch (e) {
      setStatus('error')
      addEvent('error', { message: e instanceof Error ? e.message : String(e) })
      // Symmetric cleanup on startCall failure: ensure the ref isn't
      // left dangling pointing at a half-initialised client.
      clientRef.current = null
      throw e
    }
  }, [agentType, addEvent, addMessage, handleRetellUpdate, resetTranscriptDiff])

  const disconnect = useCallback(async () => {
    if (!clientRef.current) return
    setStatus('disconnecting')
    addEvent('disconnecting')
    const active = clientRef.current
    if (active.provider === 'vapi') {
      active.instance.stop()
    } else {
      active.instance.stopCall()
    }
    clientRef.current = null
    setStatus('idle')
    setIsSpeaking(false)
  }, [addEvent])

  // Cleanup on unmount. Dispatch via the discriminated tag so we never
  // invoke the wrong teardown method.
  useEffect(() => {
    return () => {
      const active = clientRef.current
      if (!active) return
      if (active.provider === 'vapi') {
        active.instance.stop()
      } else {
        active.instance.stopCall()
      }
      clientRef.current = null
    }
  }, [])

  return {
    status,
    provider,
    agentType,
    setAgentType,
    messages,
    events,
    isSpeaking,
    connect,
    disconnect,
  }
}
